/**
 * useFeatureFlag — read a single flag's value for the current tenant
 * scope.
 *
 * Per CHAP-177 (Phase 1N #149). Backed by React Query cache;
 * /api/feature-flags is hit once per tenant + cached 1 min (per AC —
 * toggling a flag in the admin UI propagates to clients within 1 min).
 * Tenant flag wins over global flag with the same key (per server
 * merge rule in routes/admin/feature-flags.ts).
 *
 * Usage:
 *   const enabled = useFeatureFlag<boolean>('voting-v2');
 *   if (enabled) { ... }
 *
 *   const config = useFeatureFlag<{ enabled: boolean; tier_cap: string }>('experiment-x');
 *   if (config?.enabled) { ... }
 */

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';

interface FeatureFlagsResponse {
  flags: Record<string, unknown>;
  scope: 'tenant' | 'global';
}

function useAllFlags() {
  // CHAP-177: hit the tenant-scoped path when in a /c/:slug context so
  // per-tenant flags resolve; fall back to global flags otherwise.
  const { slug } = useParams<{ slug?: string }>();
  const path = slug ? `/api/c/${slug}/feature-flags` : '/api/feature-flags';
  return useQuery<FeatureFlagsResponse, Error>({
    queryKey: ['feature-flags', slug ?? 'global'],
    queryFn: () => apiGet<FeatureFlagsResponse>(path),
    staleTime: 60_000, // 1 min per CHAP-177 AC
  });
}

/**
 * Read a feature flag. Returns undefined while loading, then either:
 *   - the typed value if the flag is set
 *   - undefined if the flag is unset (defaults to "off"-ish for the
 *     caller's interpretation)
 *
 * Type-parameterize per call site so callers get strong types without
 * casting at every read.
 */
export function useFeatureFlag<T = unknown>(key: string): T | undefined {
  const { data } = useAllFlags();
  if (!data) return undefined;
  return data.flags[key] as T | undefined;
}

/**
 * Read a boolean flag — convenience for the common case. Returns
 * `false` for unset OR when loading (so UI gates stay closed by
 * default rather than briefly showing a feature then hiding it).
 */
export function useFeatureFlagBoolean(key: string): boolean {
  const value = useFeatureFlag<unknown>(key);
  return Boolean(value);
}
